/* eslint-disable max-lines */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
} from '@mui/material';
import { Control, Controller, FieldErrors, Path, get } from 'react-hook-form';

const multipliers = [1440, 60, 1];
const maximums = [99, 23, 59]; // day max changed to 99

const minutesToParts = (value: unknown): [number, number, number] => {
  let number = 0;
  if (typeof value === 'string') number = parseInt(value, 10);
  if (typeof value === 'number') number = value;
  const days = Math.floor(number / multipliers[0]);
  const daysReminder = number % multipliers[0];
  const hours = Math.floor(daysReminder / multipliers[1]);
  const minutes = daysReminder % multipliers[1];
  return [days, hours, minutes];
};

interface DurationInputProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  errors?: FieldErrors<T>;
}

interface OutlinedNumberFieldProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

interface DurationInputFieldsProps {
  name: string;
  label: string;
  value: unknown;
  onChange: (value: number) => void;
  error?: Record<string, unknown>;
  dayLabel: string;
  hourLabel: string;
  minuteLabel: string;
}

const adornmentSx = {
  flexDirection: 'column',
  maxHeight: 'unset',
  alignSelf: 'stretch',
  borderLeft: '1px solid',
  borderColor: 'divider',
  ml: 0,
  '& button': { py: 0, flex: 1, borderRadius: 0.5 },
};

const NumberFieldEndAdornment: React.FC<{ label: string }> = ({ label }) => (
  <InputAdornment position="end" sx={adornmentSx}>
    <BaseNumberField.Increment
      render={<IconButton size="small" aria-label={`increase ${label}`} />}
    >
      <KeyboardArrowUpIcon fontSize="small" />
    </BaseNumberField.Increment>
    <BaseNumberField.Decrement
      render={<IconButton size="small" aria-label={`decrease ${label}`} />}
    >
      <KeyboardArrowDownIcon fontSize="small" />
    </BaseNumberField.Decrement>
  </InputAdornment>
);

interface NumberFieldInputRenderProps {
  props: React.ComponentPropsWithRef<'input'>;
  inputValue: string;
  label: string;
}

const NumberFieldInputRender: React.FC<NumberFieldInputRenderProps> = ({
  props,
  inputValue,
  label,
}) => (
  <OutlinedInput
    label={label}
    inputRef={props.ref}
    value={inputValue}
    onBlur={props.onBlur}
    onChange={props.onChange}
    onKeyUp={props.onKeyUp}
    onKeyDown={props.onKeyDown}
    onFocus={props.onFocus}
    slotProps={{ input: { ...props, style: { textAlign: 'center' } } }}
    endAdornment={<NumberFieldEndAdornment label={label} />}
  />
);

const OutlinedNumberField: React.FC<OutlinedNumberFieldProps> = ({
  id,
  label,
  value,
  min,
  max,
  onChange,
}) => (
  <BaseNumberField.Root
    value={value}
    min={min}
    max={max}
    onValueChange={(nextValue) => onChange(nextValue ?? 0)}
  >
    <FormControl fullWidth variant="outlined">
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <BaseNumberField.Input
        id={id}
        render={(props, state) => (
          <NumberFieldInputRender
            props={props}
            inputValue={state.inputValue}
            label={label}
          />
        )}
      />
    </FormControl>
  </BaseNumberField.Root>
);

const toMinutes = (days: number, hours: number, minutes: number): number =>
  days * multipliers[0] + hours * multipliers[1] + minutes;

const getNextPartsValue = (
  part: 'days' | 'hours' | 'minutes',
  nextValue: number,
  days: number,
  hours: number,
  minutes: number,
): number => {
  const nextDays = part === 'days' ? nextValue : days;
  const nextHours = part === 'hours' ? nextValue : hours;
  const nextMinutes = part === 'minutes' ? nextValue : minutes;
  return toMinutes(nextDays, nextHours, nextMinutes);
};

const getErrorMessage = (
  error?: Record<string, unknown>,
): string | undefined =>
  typeof error?.message === 'string' ? error.message : undefined;

const DurationInputFields: React.FC<DurationInputFieldsProps> = ({
  name,
  label,
  value,
  onChange,
  error,
  dayLabel,
  hourLabel,
  minuteLabel,
}) => {
  const [days, hours, minutes] = minutesToParts(value);
  const handlePartChange =
    (part: 'days' | 'hours' | 'minutes') => (nextValue: number) =>
      onChange(getNextPartsValue(part, nextValue, days, hours, minutes));

  return (
    <div className="form-control">
      <FormLabel error={Boolean(error)}>{label}</FormLabel>
      <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
        <OutlinedNumberField
          id={`${name}-days`}
          label={dayLabel}
          value={days}
          min={0}
          max={maximums[0]}
          onChange={handlePartChange('days')}
        />
        <OutlinedNumberField
          id={`${name}-hours`}
          label={hourLabel}
          value={hours}
          min={0}
          max={maximums[1]}
          onChange={handlePartChange('hours')}
        />
        <OutlinedNumberField
          id={`${name}-minutes`}
          label={minuteLabel}
          value={minutes}
          min={0}
          max={maximums[2]}
          onChange={handlePartChange('minutes')}
        />
      </Stack>
      <FormHelperText error={Boolean(error)}>
        {getErrorMessage(error)}
      </FormHelperText>
    </div>
  );
};

export const DurationInput = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  name,
  label,
  control,
  errors,
}: DurationInputProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  const day = String(t('global.durations.short.day', { count: 10 }));
  const hour = String(t('global.durations.short.hour', { count: 10 }));
  const minute = String(t('global.durations.short.minute', { count: 10 }));

  const error = get(errors, name);

  return (
    <Controller
      name={name}
      render={({ field: { onChange, value } }) => (
        <DurationInputFields
          name={name}
          label={label}
          value={value}
          onChange={onChange}
          error={error}
          dayLabel={day}
          hourLabel={hour}
          minuteLabel={minute}
        />
      )}
      control={control}
    />
  );
};
